import { cx, DemoRibbon, inr, Packshot, Sheet, timeAgo } from '@runnerboy/ui';
import { Bell, ChevronDown, ChevronRight, Home, MapPin, Receipt, Search, User } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation, useNavigate, useNavigationType } from 'react-router';
import { client, useAsync, useSession } from '../lib/session';

export function useOnline() {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/** Routes that are a focused flow: no tab bar, no floating cart, nothing competing with the task. */
/** New screens open at the top; back/forward (POP) keeps the browser's position. */
export function ScrollToTop() {
  const { pathname } = useLocation();
  const type = useNavigationType();
  useLayoutEffect(() => {
    if (type !== 'POP') window.scrollTo(0, 0);
  }, [pathname, type]);
  return null;
}

export const FLOW_ROUTES = (path: string) => path === '/cart' || path === '/checkout' || path.startsWith('/pay') || /^\/orders\/[^/]+/.test(path);

const coordsLabel = /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/;

interface InAppNotification {
  id: string;
  title: string;
  body: string;
  readAt: string | null;
  createdAt: string;
}

export function TopBar({ onLocation }: { onLocation: () => void }) {
  const { t } = useTranslation();
  const { location, availability, user } = useSession();
  const nav = useNavigate();
  const loc = useLocation();
  const [bellOpen, setBellOpen] = useState(false);
  const notes = useAsync((s) => client.get<InAppNotification[]>('/me/notifications', s), [user?.id, loc.pathname === '/'], { enabled: !!user });
  const unread = (notes.data ?? []).filter((n) => !n.readAt).length;
  const dark = loc.pathname === '/' || loc.pathname === '/road-safety';
  const headerRef = useRef<HTMLElement>(null);
  // Publish the header height so sticky sub-headers (catalogue filters) sit exactly below it.
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const set = () => document.documentElement.style.setProperty('--rb-header-h', `${el.getBoundingClientRect().height}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  });
  if (FLOW_ROUTES(loc.pathname))
    return (
      <header ref={headerRef} className={cx('rb-safe-top sticky top-0 z-30', availability?.jurisdiction?.isDemo ? 'bg-night-900' : 'bg-paper')}>
        {availability?.jurisdiction?.isDemo && <DemoRibbon />}
      </header>
    );
  const label = location ? (coordsLabel.test(location.label) ? t('location.pinned', 'Pinned location') : location.label) : t('location.choose');
  return (
    <header ref={headerRef} className={cx('rb-safe-top sticky top-0 z-30 backdrop-blur-xl', dark ? 'bg-night-900/95' : 'border-b border-ink-900/[0.06] bg-paper/90')}>
      {availability?.jurisdiction?.isDemo && <DemoRibbon />}
      <div className="mx-auto flex max-w-3xl items-center gap-2 px-4 py-2.5">
        <button onClick={onLocation} className="rb-press flex min-w-0 flex-1 items-center gap-2.5 rounded-full py-1 pr-2 text-left" aria-label={t('location.choose')}>
          <span className={cx('grid h-10 w-10 shrink-0 place-items-center rounded-full', dark ? 'bg-marigold-500 text-ink-950' : 'bg-ink-900 text-marigold-500')}>
            <MapPin className="h-[19px] w-[19px]" strokeWidth={2.4} aria-hidden />
          </span>
          <span className="min-w-0">
            <span className={cx('rb-eyebrow flex items-center gap-1 text-[10px]', dark ? 'text-white/60' : 'text-ink-500')}>
              {t('location.deliverTo')} <ChevronDown className="h-3 w-3" aria-hidden />
            </span>
            <span className={cx('block truncate text-[16px] font-bold tracking-[-0.01em]', dark ? 'text-white' : 'text-ink-900')}>{label}</span>
          </span>
        </button>
        {user && (
          <button onClick={() => setBellOpen(true)} className={cx('rb-press relative grid h-10 w-10 shrink-0 place-items-center rounded-full', dark ? 'rb-glass text-white' : 'bg-white text-ink-800 shadow-[var(--shadow-card)]')} aria-label={unread ? `${unread} unread notifications` : 'Notifications'}>
            <Bell className="h-[19px] w-[19px]" aria-hidden />
            {unread > 0 && <span className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full bg-marigold-500 ring-2 ring-night-900" aria-hidden />}
          </button>
        )}
        <button onClick={() => nav('/account')} className={cx('rb-press grid h-10 w-10 shrink-0 place-items-center rounded-full text-[15px] font-bold', dark ? 'bg-white text-ink-900' : 'bg-ink-900 text-white')} aria-label={t('nav.account')}>
          {user?.name ? user.name.charAt(0).toUpperCase() : <User className="h-[19px] w-[19px]" aria-hidden />}
        </button>
      </div>
      <Sheet open={bellOpen} onClose={() => setBellOpen(false)} title={t('nav.notifications', 'Notifications')}>
        {(notes.data ?? []).length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-500">{t('notifications.empty', 'You’re all caught up.')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {(notes.data ?? []).slice(0, 20).map((n) => (
              <li key={n.id} className={cx('rounded-[16px] p-3.5', n.readAt ? 'bg-ink-50' : 'bg-marigold-50 ring-1 ring-marigold-200')}>
                <div className="flex items-start justify-between gap-3">
                  <span className="text-[14px] font-semibold text-ink-900">{n.title}</span>
                  <span className="rb-num shrink-0 text-[11px] text-ink-400">{timeAgo(n.createdAt)}</span>
                </div>
                <p className="mt-0.5 text-[13px] text-ink-600">{n.body}</p>
              </li>
            ))}
          </ul>
        )}
      </Sheet>
      <MarkRead open={bellOpen} notes={notes.data ?? []} />
    </header>
  );
}

function MarkRead({ open, notes }: { open: boolean; notes: InAppNotification[] }) {
  useEffect(() => {
    if (!open) return;
    for (const n of notes.filter((x) => !x.readAt).slice(0, 20)) void client.post(`/me/notifications/${n.id}/read`).catch(() => undefined);
  }, [open, notes]);
  return null;
}

export function BottomNav() {
  const { t } = useTranslation();
  const loc = useLocation();
  if (FLOW_ROUTES(loc.pathname)) return null;
  const items = [
    { to: '/', icon: Home, label: t('nav.home'), end: true },
    { to: '/search', icon: Search, label: t('nav.search') },
    { to: '/orders', icon: Receipt, label: t('nav.orders') },
    { to: '/account', icon: User, label: t('nav.account') },
  ];
  return (
    <nav className="fixed inset-x-0 z-30 px-3" style={{ bottom: 'max(10px, calc(env(safe-area-inset-bottom) + 6px), calc(var(--safe-area-inset-bottom, 0px) + 6px))' }} aria-label="Main">
      <div className="mx-auto grid h-[66px] max-w-md grid-cols-4 rounded-[26px] bg-ink-900/[0.96] px-1.5 shadow-[var(--shadow-float)] ring-1 ring-white/[0.06] backdrop-blur-xl">
        {items.map((i) => (
          <NavLink key={i.to} to={i.to} end={i.end} className="rb-press flex flex-col items-center justify-center gap-1 rounded-[20px] text-[11px] font-semibold tracking-[0.01em]">
            {({ isActive }) => (
              <>
                <span className={cx('relative grid h-8 w-12 place-items-center rounded-full transition-colors duration-200', isActive ? 'bg-marigold-500 text-ink-950' : 'text-white/55')}>
                  <i.icon className="h-[21px] w-[21px]" strokeWidth={isActive ? 2.4 : 2} aria-hidden />
                </span>
                <span className={isActive ? 'text-white' : 'text-white/55'}>{i.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export function CartBar() {
  const { t } = useTranslation();
  const { cart } = useSession();
  const nav = useNavigate();
  const loc = useLocation();
  if (!cart || cart.items.length === 0 || FLOW_ROUTES(loc.pathname)) return null;
  const count = cart.items.reduce((s, i) => s + i.quantity, 0);
  return (
    <div className="fixed inset-x-0 z-30 px-4" style={{ bottom: 'calc(88px + max(env(safe-area-inset-bottom), var(--safe-area-inset-bottom, 0px)))' }}>
      <button
        onClick={() => nav('/cart')}
        className="rb-press mx-auto flex w-full max-w-md animate-[slide-up_320ms_cubic-bezier(0.16,1,0.3,1)] items-center gap-3 rounded-[22px] bg-marigold-500 py-2 pl-2 pr-4 text-left text-ink-950 shadow-[var(--shadow-glow)]"
      >
        <span className="flex -space-x-3">
          {cart.items.slice(0, 3).map((i) => (
            <span key={i.storeProductId} className="grid h-11 w-11 place-items-center rounded-[14px] bg-white ring-2 ring-marigold-500">
              <Packshot brand={i.product.brand} name={i.product.name} categorySlug={i.product.categorySlug} volumeMl={i.product.volumeMl} className="h-10 w-10" />
            </span>
          ))}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[15px] font-bold leading-tight">
            {t('common.items', { count })} · <span className="rb-num">{inr(cart.items.reduce((s, i) => s + i.lineTotal, 0))}</span>
          </span>
          <span className="block truncate text-[12px] font-medium text-ink-900/70">{t('cart.fromStore', { store: cart.storeName })}</span>
        </span>
        <span className="flex items-center gap-0.5 rounded-full bg-ink-900 px-3 py-2 text-[13px] font-bold text-white">
          {t('cart.viewCart')} <ChevronRight className="h-4 w-4" aria-hidden />
        </span>
      </button>
    </div>
  );
}

export function Page({ children, className, wide, flow }: { children: ReactNode; className?: string; wide?: boolean; flow?: boolean }) {
  return (
    // A caller's own pt-* replaces the default top padding (two pt utilities would conflict unpredictably).
    <main className={cx('mx-auto px-4', /(^|\s)!?pt-/.test(className ?? '') ? null : 'pt-4', flow ? 'pb-[calc(8rem+env(safe-area-inset-bottom))]' : 'pb-[calc(11rem+env(safe-area-inset-bottom))]', wide ? 'max-w-5xl' : 'max-w-3xl', className)}>
      {children}
    </main>
  );
}

export function OfflineBanner() {
  const online = useOnline();
  const { t } = useTranslation();
  if (online) return null;
  return (
    <div role="status" className="rb-safe-top sticky top-0 z-40 bg-ink-900 px-4 py-2 text-center text-xs font-semibold text-white">
      {t('common.offline')}
    </div>
  );
}

export function SectionTitle({ children, action, eyebrow, className }: { children: ReactNode; action?: ReactNode; eyebrow?: ReactNode; className?: string }) {
  return (
    <div className={cx('mb-3.5 flex items-end justify-between gap-3', /(^|\s)!?mt-/.test(className ?? '') ? null : 'mt-9', className)}>
      <div className="min-w-0">
        {eyebrow && <div className="rb-eyebrow mb-1 text-ink-400">{eyebrow}</div>}
        <h2 className="rb-display text-[22px] font-bold leading-tight text-ink-900">{children}</h2>
      </div>
      {action}
    </div>
  );
}

export function SeeAll({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} className="rb-press inline-flex shrink-0 items-center gap-0.5 rounded-full bg-white px-3 py-1.5 text-[13px] font-semibold text-ink-900 shadow-[var(--shadow-card)]">
      {label} <ChevronRight className="h-4 w-4" aria-hidden />
    </Link>
  );
}

export function ResponsibleNote() {
  const { t } = useTranslation();
  return (
    <div className="mt-10 rounded-[22px] bg-night-900 p-5 text-white">
      <div className="flex items-center gap-2">
        <span className="rb-num grid h-8 min-w-8 place-items-center rounded-full bg-marigold-500 px-2 text-[12px] font-bold text-ink-950">21+</span>
        <span className="rb-eyebrow text-white/60">{t('responsible.title', 'Responsible service')}</span>
      </div>
      <p className="mt-3 text-[13px] leading-relaxed text-white/75">{t('responsible.notice')}</p>
    </div>
  );
}
